import {
  OwnUpHeadlineBook,
  OwnUpSmallSubheadlineMedium,
  OwnUpSubheadlineMedium,
  OwnUpBody
} from '@rategravity/own-up-component-library';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { imageUrlFor } from '../../lib/image-url';
import slugify from 'slugify'; // Import slugify from a library (e.g., slugify)

interface TableRow {
  _key: string;
  cells: string[];
}

interface OwnupTableProps {
  value: {
    rows: TableRow[];
  };
  href?: string;
}

interface OwnupImageWrapperProps {
  value: {
    asset: {
      url: string;
    };
    alt: string;
  };
}

const generateAnchorSlug = (children: any) => {
  const child = children && children?.[0];
  const text = child?.props?.text || child || '';
  return slugify(text, { lower: true }) || '';
};

const OwnUpH1Wrapper = (children: ReactNode) => (
  <OwnUpHeadlineBook variant="h1">{children}</OwnUpHeadlineBook>
);

const OwnUpH2Wrapper = (children: ReactNode) => (
  <OwnUpSubheadlineMedium
    id={generateAnchorSlug(children)}
    variant="h2"
    style={{ fontSize: '2rem', lineHeight: '2.5rem', scrollMarginTop: '40px' }}
  >
    {children}
  </OwnUpSubheadlineMedium>
);

const OwnUpH3Wrapper = (children: ReactNode) => {
  return (
    <OwnUpSmallSubheadlineMedium
      id={generateAnchorSlug(children)}
      variant="h3"
      style={{ fontSize: '1.5rem', lineHeight: '2rem', scrollMarginTop: '40px' }}
    >
      {children}
    </OwnUpSmallSubheadlineMedium>
  );
};

const OwnUpSubtitleWrapper = (children: ReactNode) => (
  <OwnUpBody variant="body1" className="tagline">
    {children}
  </OwnUpBody>
);

const OwnupStrongWrapper = (props: Record<string, any>, children: ReactNode) => (
  <strong {...props} className="highlight">
    {children}
  </strong>
);

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: 20px;
  + p {
    padding-top: 20px !important;
  }
`;

const TableWrapper = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  border: 1px solid #99acc2;
`;

const TableBodyWrapper = styled.tbody``;

const TableRowWrapper = styled.tr`
  height: 37px;
`;

const TableCellWrapper = styled.td`
  height: 37px;
  padding: 4px;
  border: 1px solid #99acc2;
`;

const Strong = styled.strong``;

const OwnupTableWrapper: React.FC<OwnupTableProps> = ({ value, href }) => {
  // Adding this customOption on the basis of url because we don't have such options in @sanity/table.
  const customOptions = href && href === 'women-pay-more-for-their-mortgage-than-men';
  const renderTableRows = () => {
    return value?.rows.map((row: TableRow, rowIndex: number) => (
      <TableRowWrapper key={row?._key}>
        {row?.cells?.map(
          (cell: string, cellIndex: number) =>
            cell && (
              <TableCellWrapper key={cellIndex} colSpan={rowIndex === 0 && customOptions ? 3 : 0}>
                {rowIndex === 0 || (rowIndex === 1 && customOptions) ? (
                  <Strong>{cell}</Strong>
                ) : (
                  cell
                )}
              </TableCellWrapper>
            )
        )}
      </TableRowWrapper>
    ));
  };

  return (
    <TableContainer>
      <TableWrapper>
        <TableBodyWrapper>{renderTableRows()}</TableBodyWrapper>
      </TableWrapper>
    </TableContainer>
  );
};

const ImageWrapper = styled.p``;

const Image = styled.img`
  /* Add your styles here */
`;

const OwnupImageWrapper: React.FC<OwnupImageWrapperProps> = ({ value }) => {
  let imageUrl: any = value?.asset?.url;
  if (!imageUrl?.startsWith('https')) {
    imageUrl = imageUrlFor(value);
  }
  return (
    <ImageWrapper>
      <Image src={imageUrl} alt={value.alt || ' '} loading="lazy" />
    </ImageWrapper>
  );
};

export const portableTextWrappers = {
  h1: OwnUpH1Wrapper,
  h2: OwnUpH2Wrapper,
  h3: OwnUpH3Wrapper,
  p: OwnUpSubtitleWrapper,
  strong: OwnupStrongWrapper,
  table: OwnupTableWrapper,
  img: OwnupImageWrapper
};
// c.f. https://github.com/netlify/netlify-cms/blob/master/packages/netlify-cms-locales/src/en/index.js
export const updateHeadingNames = (lang: Record<string, any>) => {
  lang.editor.editorWidgets.headingOptions = {
    headingOne: 'Headline',
    headingTwo: 'Subheadline',
    headingThree: 'Small Subheadline'
  };
  return lang;
};
